/**
 * @fileoverview The single sink every error goes through.
 * @module lib/errors/logger
 *
 * @description
 * One function to call from anywhere, one place to later plug Sentry / a
 * collector into. In development it prints a readable group; in production it
 * stays quiet on the console and forwards to the registered reporter, if any.
 */

import { env } from "@/config/env"

import type { AppError } from "./AppError"
import { normalizeError } from "./normalizeError"

/** Where the error was caught. Helps triage without reading the stack. */
export type ErrorSource =
	"router" | "boundary" | "http" | "window" | "promise" | "manual"

/** Payload handed to the external reporter. */
export interface ErrorReport {
	readonly error: AppError
	readonly source: ErrorSource
	readonly timestamp: string
}

/** Hook invoked for every reported error. */
export type ErrorReporter = (report: ErrorReport) => void

let reporter: ErrorReporter | null = null

/**
 * Registers the external reporter (Sentry, custom collector, ...).
 *
 * @param next - The reporter, or null to unregister.
 */
export function setErrorReporter(next: ErrorReporter | null): void {
	reporter = next
}

/**
 * Reports an error through the central sink.
 *
 * @param error - Any caught value; it is normalised on the way in.
 * @param source - Where the error was caught.
 * @returns The normalised AppError, so callers can keep using it.
 *
 * @example
 * const appError = logError(error, "http")
 */
export function logError(
	error: unknown,
	source: ErrorSource = "manual"
): AppError {
	const appError = normalizeError(error)
	const report: ErrorReport = {
		error: appError,
		source,
		timestamp: new Date().toISOString(),
	}

	if (env.isDevelopment) {
		console.error(
			`[dailypex:${source}] ${appError.code} ${appError.message}`,
			appError.toJSON(),
			appError.cause ?? ""
		)
	}

	if (reporter !== null) {
		try {
			reporter(report)
		} catch {
			// A failing reporter must never mask the original error.
		}
	}

	return appError
}

/**
 * Installs the catch-all listeners for errors no boundary saw.
 *
 * @description
 * Render errors are caught by the ErrorBoundary and loader errors by the
 * router; this covers the rest — event handlers, timers, floating promises.
 *
 * @returns A teardown function removing both listeners.
 */
export function registerGlobalErrorHandlers(): () => void {
	const onError = (event: ErrorEvent): void => {
		logError(event.error ?? event.message, "window")
	}

	const onUnhandledRejection = (event: PromiseRejectionEvent): void => {
		logError(event.reason, "promise")
	}

	window.addEventListener("error", onError)
	window.addEventListener("unhandledrejection", onUnhandledRejection)

	return () => {
		window.removeEventListener("error", onError)
		window.removeEventListener("unhandledrejection", onUnhandledRejection)
	}
}
